import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { RoomInfoProducts, RoomProduct } from "@b2bportal/lodging-api";
import { Dialog, DialogContent, DialogTrigger, Icon } from "@tenant/ui";
import clsx from "clsx";
import { AmenityIcon } from "../AmenityIcon";
import { useHotelRoomCardDialog } from "./hooks";
import defaultStyles from "./HotelRoomCardDialog.module.scss";

export interface HotelRoomCardDialogComponentProps
  extends PlatformComponentProps {
  room: RoomInfoProducts;
  nightCount: number;
  /** The rate the user has selected on the card. Benefits are rate-level, so the
   * Benefits section reflects this rate (falls back to the first rate). */
  selectedProduct?: RoomProduct;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelRoomCardDialog;

export const HotelRoomCardDialog = (
  props: HotelRoomCardDialogComponentProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { className, children } = props;
  const {
    context,
    text,
    handlers: { emitOpenDetailsEvent },
  } = useHotelRoomCardDialog(props);

  return (
    <Dialog>
      <DialogTrigger
        className={clsx(cn("room-details-link"), className)}
        onClick={() => emitOpenDetailsEvent()}
      >
        {text.viewDetails}
      </DialogTrigger>
      <DialogContent title={text.roomDetailsTitle}>
        <div className={clsx(cn("content"))}>
          <div className={cn("section", ["hotel-info"])}>
            {context.detailsImage != null ? (
              // eslint-disable-next-line jsx-a11y/alt-text
              <img className={cn("image")} src={context.detailsImage?.url} />
            ) : null}
            <div className={cn("details")}>
              <div className={cn("title")}>{text.roomName}</div>
              <div className={cn("detail", ["guests"])}>
                <Icon className={cn("icon")} iconName={IconNameEnum.guest} />
                {text.guests}
              </div>
              <div className={cn("detail", ["beds"])}>
                <Icon className={cn("icon")} iconName={IconNameEnum.bed} />
                {text.beds}
              </div>
            </div>
          </div>

          <div className={cn("section", ["amenities"])}>
            <h4 className={cn("title")}>{text.amenitiesTitle}</h4>
            <ul className={cn("amenity-list")}>
              {context.amenities.map(({ amenity, displayName }) => (
                <li key={amenity} className={cn("amenity-item")}>
                  <AmenityIcon
                    className={cn("amenity-icon")}
                    amenity={amenity}
                    alt={displayName}
                  />
                  {displayName}
                </li>
              ))}
            </ul>
          </div>

          {context.benefits.length > 0 ? (
            <div className={cn("section", ["benefits"])}>
              <h4 className={cn("title")}>{text.benefitsTitle}</h4>
              <ul className={cn("benefit-list")}>
                {context.benefits.map((benefit) => (
                  <li key={benefit.id} className={cn("benefit-item")}>
                    <Icon
                      className={cn("benefit-icon")}
                      iconName={IconNameEnum.checkCircle}
                    />
                    {benefit.translatedName || benefit.name}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {text.roomDescription != null && text.roomDescription !== "" ? (
            <div className={cn("section", ["description"])}>
              <h4 className={cn("title")}>{text.roomDescriptionTitle}</h4>
              <div
                className={cn("description-content")}
                dangerouslySetInnerHTML={{ __html: text.roomDescription }}
              />
            </div>
          ) : null}
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};
